import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import UnityApi from '../providers/core/api';
import UserConfiguration from '../providers/settings';
import { useAuth } from 'react-oidc-context';
import {
  getSiteSettings,
  getFeatures,
  setSiteSettings,
} from '../providers/authService';
import {
  applyTelemetryFeatureFlag,
  clearTelemetryUser,
  logger,
  setTelemetryUser,
} from '../lib/telemetry';

const permissionLevel = {
  all: ['read', 'write', 'delete'],
  readWrite: ['read', 'write'],
  readOnly: ['read'],
};

export const UserContext = React.createContext();

export const UserProvider = ({ children }) => {
  const auth = useAuth();
  const [user, setUser] = useState({});
  const [myTeam, setTeam] = useState([]);
  const [roles, setRoles] = useState([]);
  const [roleActions, setRoleActions] = useState([]);
  const [environment, setEnvironment] = useState('');
  const [features, setFeatures] = useState({});
  const [searchState, setSearchState] = useState(null);
  const [registrationLocation, setRegistrationLocation] = useState(null);
  const [canEmail, setCanEmail] = useState(false);
  const [archivedUsers, setArchivedUsers] = useState([]);
  const [configVersion, setConfigVersion] = useState('');

  const fetchUser = async () => {
    const newUser = await new UnityApi('user/current').request();
    setUser(newUser);
  };

  const fetchTeam = async () => {
    const team = await new UnityApi('user/MyTeam').request();
    setTeam(team.map((u) => ({ userId: u.id, userName: u.userName })));
  };

  const fetchRoles = async () => {
    const currentRoles = await new UnityApi('role/current').request();
    setRoles(currentRoles);
  };

  const fetchRoleActions = async () => {
    const actions = await new UnityApi('role/roleActions').request();
    setRoleActions(
      actions.map((x) => ({
        ...x,
        permissions: permissionLevel[x.permissionLevel],
      }))
    );
  };

  const fetchArchivedUsers = async () => {
    const users = await new UnityApi('user/archived').request();
    setArchivedUsers(users);
  };

  useEffect(() => {
    if (auth.user.profile.idp === 'aad') {
      setCanEmail(true);
    }
  }, [auth.user]);

  // Stamp telemetry with the OIDC `sub` -- an opaque stable id. Not preferred_username,
  // which is the email address the API reads for UserName.
  useEffect(() => {
    if (auth.isAuthenticated && auth.user) {
      setTelemetryUser(auth.user);
    } else {
      clearTelemetryUser();
    }
  }, [auth.isAuthenticated, auth.user]);

  const fetchSettings = async () => {
    const settings = getSiteSettings();
    setEnvironment(settings?.environment ?? '');
    setConfigVersion(settings?.configVersion ?? '');
    // getFeatures returns a new object every call, so keep the current one when
    // nothing changed. Otherwise polling would re-render every consumer of this
    // context and unmount anything they render, such as an open email window.
    setFeatures((current) => {
      const latest = getFeatures();
      return JSON.stringify(current) === JSON.stringify(latest)
        ? current
        : latest;
    });
  };

  const refreshSettings = async () => {
    try {
      const response = await fetch('/settings');
      if (response.ok) {
        const settings = await response.json();
        setSiteSettings(settings);
        setEnvironment(settings?.environment ?? '');
        setFeatures(settings?.features ?? {});
        setConfigVersion(settings?.configVersion ?? '');
        // This is the only path that actually re-fetches /settings (fetchSettings below just
        // re-reads localStorage), so it is where a feature-flag change can take effect without
        // a reload -- making the flag a usable kill switch mid-session.
        applyTelemetryFeatureFlag(settings);
      }
    } catch (error) {
      // Transient and retried by the caller, so a warning rather than an error.
      logger.warn(
        'Settings refresh failed',
        { Component: 'UserProvider', Operation: 'RefreshSettings' },
        error
      );
    }
  };

  const fetchUserLocation = async () => {
    const id = await new UserConfiguration().get('registration-location');
    if (id) {
      const location = await new UnityApi(
        `registrationLocation/${id}`
      ).request();

      if (location) {
        setRegistrationLocation(location);
        return;
      }
    }

    const unknownLocation = await new UnityApi(
      'registrationLocation/default'
    ).request();
    setRegistrationLocation(unknownLocation);
  };

  useEffect(() => {
    fetchUser();
    fetchTeam();
    fetchRoles();
    fetchSettings();
    fetchRoleActions();
    fetchUserLocation();
    fetchArchivedUsers();
  }, []);

  // Poll for settings updates (includes feature flags) every 30 seconds
  // to match the backend's Sentinel key polling interval
  useEffect(() => {
    const interval = setInterval(() => {
      fetchSettings();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, []);

  return (
    <UserContext.Provider
      value={{
        ...user,
        myTeam,
        roleNames: roles,
        environment,
        features,
        roleActions,
        searchState,
        setSearchState,
        registrationLocation,
        setRegistrationLocation,
        fetchUserLocation,
        refreshSettings,
        configVersion,
        canEmail,
        archivedUsers,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

UserProvider.propTypes = {
  children: PropTypes.element.isRequired,
};
